import React, { useEffect, useId, useRef, useState } from "react";
import { INITIAL_BOOK } from "../../data/bookshelf";

// The shelf: real spines in a row, one book turned to show its cover, and one
// takeaway card that always shows the selected book.
//
// One book is always selected, Thinking in Systems to begin with. Pointing at
// a book (mouse or pen), focusing it from the keyboard, or tapping, clicking
// or pressing it selects it at once; nothing waits, closes or expires, and
// choosing the selected book again keeps it. The card changes in the same
// update, while the books turn.
//
// Each book is a small CSS 3D object: the cover plane and the spine plane at
// right angles, turned around the edge they share. Every open book takes its
// spine width plus the same slack (the widest cover's extra width), so the row
// never changes length and the book under the pointer stays under it while
// the others turn. Keyboard and tap selections also scroll a narrow shelf
// sideways to show the book, once it has turned; hover never scrolls.

export const ROTATE_MS = 260;
const REVEAL_DELAY_MS = 300;

const isHover = (event) => event.pointerType === "mouse" || event.pointerType === "pen";

function isKeyboardFocus(node) {
  try {
    return node.matches(":focus-visible");
  } catch (error) {
    return false;
  }
}

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Bookshelf({ shelf, initial = INITIAL_BOOK }) {
  const { books } = shelf;
  const baseId = useId();
  const cardId = `${baseId}-card`;
  const headingId = `${baseId}-heading`;
  const [selected, setSelected] = useState(() =>
    books.some((book) => book.id === initial) ? initial : books[0] && books[0].id
  );
  const [leaving, setLeaving] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const selectedRef = useRef(selected);
  const scrollRef = useRef(null);
  const bookRefs = useRef({});
  const leaveTimer = useRef(null);
  const revealTimer = useRef(null);
  const tapped = useRef(false);

  useEffect(
    () => () => {
      clearTimeout(leaveTimer.current);
      clearTimeout(revealTimer.current);
    },
    []
  );

  const reveal = (id) => {
    const scroller = scrollRef.current;
    const node = bookRefs.current[id];
    if (!scroller || !node || scroller.scrollWidth <= scroller.clientWidth) return;
    const box = node.getBoundingClientRect();
    const left = box.left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
    const right = left + box.width;
    let target = scroller.scrollLeft;
    if (left < scroller.scrollLeft) target = left;
    else if (right > scroller.scrollLeft + scroller.clientWidth) target = right - scroller.clientWidth;
    if (target === scroller.scrollLeft) return;
    scroller.scrollTo({ left: target, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  const select = (id, { deliberate = false } = {}) => {
    const current = selectedRef.current;
    if (current !== id) {
      selectedRef.current = id;
      setSelected(id);
      setLeaving(current);
      clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => setLeaving(null), ROTATE_MS + 40);
    }
    if (!deliberate) return;
    const book = books.find((item) => item.id === id);
    setAnnouncement(`${book.title}. Key takeaway: ${book.takeaway}`);
    clearTimeout(revealTimer.current);
    revealTimer.current = setTimeout(() => reveal(id), REVEAL_DELAY_MS);
  };

  if (!books.length) return null;

  const sum = (values) => values.reduce((total, value) => total + value, 0);
  const spines = sum(books.map((book) => book.spineRatio));
  const slack = Math.max(...books.map((book) => book.coverRatio - book.spineRatio));

  return (
    <div className="pf-shelf-area">
      <h3 id={headingId} className="pf-section__title pf-shelf-area__title">
        Bookshelf
      </h3>
      <div
        className="pf-shelf-area__body"
        style={{
          "--pf-shelf-spines": spines,
          "--pf-shelf-slack": slack,
          "--pf-shelf-count": books.length,
        }}
      >
        <div ref={scrollRef} className="pf-shelf-scroll">
          <ul className="pf-shelf" aria-labelledby={headingId}>
            {books.map((book) => {
              const open = book.id === selected;
              const classes = ["pf-book"];
              if (open) classes.push("is-open");
              if (book.id === leaving) classes.push("is-leaving");
              return (
                <li
                  key={book.id}
                  className={classes.join(" ")}
                  style={{ "--pf-cover": book.coverRatio, "--pf-spine": book.spineRatio }}
                >
                  <button
                    ref={(node) => {
                      bookRefs.current[book.id] = node;
                    }}
                    type="button"
                    className="pf-book__button"
                    aria-pressed={open}
                    aria-controls={cardId}
                    onPointerEnter={(event) => isHover(event) && select(book.id)}
                    onPointerDown={(event) => {
                      tapped.current = event.pointerType === "touch";
                    }}
                    onFocus={(event) => {
                      if (isKeyboardFocus(event.currentTarget)) {
                        select(book.id, { deliberate: true });
                      }
                    }}
                    onClick={(event) => {
                      const deliberate = tapped.current || event.detail === 0;
                      tapped.current = false;
                      select(book.id, { deliberate });
                    }}
                  >
                    <span className="pf-visually-hidden">
                      {book.title}, {book.author}
                    </span>
                    <span className="pf-book__slot" aria-hidden="true">
                      <span className="pf-book__object">
                        <img
                          className="pf-book__face pf-book__face--cover"
                          src={book.coverSrc}
                          width={Math.round(576 * book.coverRatio)}
                          height="576"
                          alt=""
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                        />
                        <img
                          className="pf-book__face pf-book__face--spine"
                          src={book.spineSrc}
                          width={Math.round(576 * book.spineRatio)}
                          height="576"
                          alt=""
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                        />
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div id={cardId} className="pf-shelf-card">
          {books.map((book) => (
            <div
              key={book.id}
              className={`pf-shelf-card__entry${book.id === selected ? " is-selected" : ""}`}
              aria-hidden={book.id === selected ? undefined : "true"}
            >
              <p className="pf-shelf-card__title">{book.title}</p>
              <p className="pf-shelf-card__author">{book.author}</p>
              <div className="pf-shelf-card__rule" />
              <p className="pf-shelf-card__label">Key takeaway</p>
              <p className="pf-shelf-card__takeaway">{book.takeaway}</p>
              <p className="pf-shelf-card__explanation">{book.explanation}</p>
            </div>
          ))}
        </div>
      </div>
      <p className="pf-visually-hidden" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}

export default Bookshelf;
